import { motion } from "framer-motion";
import { useState, useRef } from "react";

const FALLBACK =
  "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85";

const optimizeImage = (url, width = 800) => {
  if (!url || typeof url !== "string") return url;
  return url.includes("?")
    ? `${url}&q=75&w=${width}&auto=format`
    : `${url}?q=75&w=${width}&auto=format`;
};

export default function PropertyCard({
  title,
  images = [],
  location,
  beds,
  baths,
  sleeps,
  badge,
  variant = "default",
}) {
  const isFeatured = variant === "featured";
  const imageList = images.length ? images : [FALLBACK];

  const [index, setIndex] = useState(0);

  const containerRef = useRef(null);

  /* 🔥 DESKTOP HOVER SCRUB */
  const handleMouseMove = (e) => {
    if (imageList.length <= 1) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;

    const newIndex = Math.floor(percent * imageList.length);
    setIndex(Math.min(newIndex, imageList.length - 1));
  };

  /* 🔥 MOBILE SWIPE */
  let touchStartX = 0;

  const handleTouchStart = (e) => {
    touchStartX = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const delta = e.changedTouches[0].clientX - touchStartX;

    if (Math.abs(delta) < 40) return;

    if (delta > 0) {
      setIndex((p) => (p - 1 + imageList.length) % imageList.length);
    } else {
      setIndex((p) => (p + 1) % imageList.length);
    }
  };

  return (
    <motion.div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.25 }}
      className="
        group relative w-full h-full
        rounded-xl overflow-hidden
        bg-bg-surface
        isolate
        ring-1 ring-bg-surface
      "
    >

      {/* IMAGE */}
      <img
        src={optimizeImage(imageList[index], isFeatured ? 1200 : 800)}
        alt={title}
        onError={(e) => (e.currentTarget.src = FALLBACK)}
        className="
          absolute inset-0 w-full h-full
          object-cover object-center
          will-change-transform
          scale-[1.02]
          transition-transform duration-500
          group-hover:scale-[1.05]
        "
      />

      {/* OVERLAY */}
      <div className="absolute inset-0 bg-gradient-overlay pointer-events-none" />

      {/* 🔥 PROGRESS BARS */}
      {imageList.length > 1 && (
        <div className="absolute top-3 left-3 right-3 flex gap-1 z-20">
          {imageList.map((_, i) => (
            <div
              key={i}
              className="flex-1 h-[2px] bg-white/30 rounded-full overflow-hidden"
            >
              <div
                className={`h-full bg-white transition-all duration-300 ${
                  i === index ? "w-full" : "w-0"
                }`}
              />
            </div>
          ))}
        </div>
      )}

      {/* BADGE */}
      {badge && (
        <div className="absolute top-3 left-3 z-20 mt-3">
          <span className="
            px-3 py-1 text-micro font-semibold rounded-pill
            bg-bg-surface/90 backdrop-blur
            border border-border-soft
            text-text-primary
          ">
            {badge}
          </span>
        </div>
      )}

      {/* CONTENT */}
      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 z-20">
        <h4 className="text-white text-sm md:text-base font-semibold">
          {title}
        </h4>

        <p className="text-white/80 text-xs mt-1">
          {location}
        </p>

        <p className="text-white/70 text-xs mt-2">
          <span className="text-white font-medium">{beds}</span> beds ·{" "}
          <span className="text-white font-medium">{baths}</span> baths ·{" "}
          <span className="text-white font-medium">{sleeps}</span> sleeps
        </p>
      </div>

    </motion.div>
  );
}